import React, { useEffect, useState } from "react";
import { useMutation } from "react-relay";
import { StatusBadge } from "src/components/ui/StatusBadge";
import { useToast } from "src/hooks/use-toast";
import { ToggleJobStatusMutation } from "src/pages/SettingsPage/mutations/ToggleJobStatusMutation";

interface JobStatusTagProps {
  jobId: string;
  status: string;
  onStatusChanged?: () => void;
  readOnly?: boolean;
}

export function JobStatusTag({ jobId, status, onStatusChanged, readOnly = false }: JobStatusTagProps) {
  const { toast } = useToast();
  const [toggleJobStatus, isToggling] = useMutation(ToggleJobStatusMutation);
  const [optimisticStatus, setOptimisticStatus] = useState(status);

  useEffect(() => {
    setOptimisticStatus(status);
  }, [status]);

  const handleClick = () => {
    if (isToggling || readOnly) return;

    const previousStatus = optimisticStatus;
    const newStatus = optimisticStatus === "ACTIVE" ? "PAUSED" : "ACTIVE";

    setOptimisticStatus(newStatus);

    toggleJobStatus({
      variables: {
        input: {
          jobId: jobId
        }
      },
      onCompleted: (response: any) => {
        if (response.toggleJobStatus?.job) {
          toast({
            title: `This is now ${response.toggleJobStatus.job.status.toLowerCase()}.`,
            variant: "success",
          });
          onStatusChanged?.();
        }
      },
      onError: (error) => {
        setOptimisticStatus(previousStatus);
        onStatusChanged?.();
        toast({
          title: "Error",
          description: error.message || "Failed to update",
          variant: "error",
        });
      }
    });
  };

  const isActive = optimisticStatus === "ACTIVE";

  return (
    <StatusBadge
      label={optimisticStatus}
      variant={isActive ? "active" : "inactive"}
      readOnly={readOnly}
      disabled={isToggling}
      onClick={readOnly ? undefined : handleClick}
      title={`Click to ${isActive ? "pause" : "activate"} job`}
      showToggleIcon
    />
  );
}
